import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const LOGO_LOCKUP = "/masandi-brand-master.png";
export const LOGO_MARK = "/masandi-brand-master.png";

const client = axios.create({ baseURL: API, withCredentials: true });

// Founder TEST/QA visibility flag (session/debug only). When true, listing GETs
// request include_test=true. Production surfaces (home/analytics) ignore it server-side.
let SHOW_TEST = false;
export function setShowTest(v) { SHOW_TEST = !!v; }

let CSRF_TOKEN = sessionStorage.getItem("masandi_csrf") || "";

export function setCsrfToken(token) {
  CSRF_TOKEN = token || "";
  if (CSRF_TOKEN) sessionStorage.setItem("masandi_csrf", CSRF_TOKEN);
  else sessionStorage.removeItem("masandi_csrf");
}

client.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (CSRF_TOKEN && ["post", "put", "patch", "delete"].includes(method)) {
    config.headers["X-CSRF-Token"] = CSRF_TOKEN;
  }
  if (SHOW_TEST && method === "get") {
    config.params = { ...(config.params || {}), include_test: true };
  }
  return config;
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = String(error.config?.url || "");
    if (status === 401 && !url.includes("/auth/login")) {
      setCsrfToken("");
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("masandi:auth-expired"));
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const api = {
  login: (email, password) => client.post("/auth/login", { email, password }).then((r) => {
    setCsrfToken(r.data.csrf_token);
    return r.data;
  }),
  me: () => client.get("/auth/me").then((r) => r.data),
  refreshCsrf: () => client.get("/auth/csrf").then((r) => {
    setCsrfToken(r.data.csrf_token);
    return r.data.csrf_token;
  }),
  logout: () => client.post("/auth/logout", {}).then((r) => {
    setCsrfToken("");
    return r.data;
  }),
  authSessions: () => client.get("/auth/sessions").then((r) => r.data),
  securityAudit: (limit = 20) => client.get("/auth/security-audit", { params: { limit } }).then((r) => r.data),
  revokeOtherSessions: () => client.post("/auth/sessions/revoke-others", {}).then((r) => r.data),
  changeFounderPassword: (current_password, new_password) => client.post("/auth/password/change", { current_password, new_password }).then((r) => { setCsrfToken(""); return r.data; }),
  home: () => client.get("/home").then((r) => r.data),
  company: () => client.get("/company").then((r) => r.data),
  systemPreferences: () => client.get("/system/preferences").then((r) => r.data),
  saveSystemPreferences: (body) => client.put("/system/preferences", body).then((r) => r.data),
  systemUsers: () => client.get("/system/users").then((r) => r.data),
  inviteStaff: (body) => client.post("/system/users/invite", body).then((r) => r.data),
  disableStaff: (id) => client.post(`/system/users/${encodeURIComponent(id)}/disable`, {}).then((r) => r.data),
  acceptInvite: (token, password) => client.post("/auth/accept-invite", { token, password }).then((r) => r.data),
  accessRoles: () => client.get("/access/roles").then((r) => r.data),
  accessAssignments: () => client.get("/access/assignments").then((r) => r.data),
  createAccessAssignment: (body) => client.post("/access/assignments", body).then((r) => r.data),
  revokeAccessAssignment: (id) => client.post(`/access/assignments/${id}/revoke`, {}).then((r) => r.data),
  departments: () => client.get("/departments").then((r) => r.data),
  agents: (params) => client.get("/agents", { params }).then((r) => r.data),
  agent: (code) => client.get(`/agents/${code}`).then((r) => r.data),
  agentTest: (code, body) => client.post(`/agents/${code}/test`, body).then((r) => r.data),
  projects: () => client.get("/projects").then((r) => r.data),
  portfolioRegistry: () => client.get("/portfolio-registry").then((r) => r.data),
  workspaceSources: () => client.get("/workspace-sources").then((r) => r.data),
  contextPackets: (project_code) => client.get("/continuity", { params: project_code ? { project_code } : {} }).then((r) => r.data),
  createContextPacket: (body) => client.post("/continuity", body).then((r) => r.data),
  approveContextPacket: (id) => client.post(`/continuity/${encodeURIComponent(id)}/approve`, {}).then((r) => r.data),
  contextPacketToTask: (id) => client.post(`/continuity/${encodeURIComponent(id)}/create-task`, {}).then((r) => r.data),
  executeGithubReadTask: (id) => client.post(`/tasks/${encodeURIComponent(id)}/execute-readonly`, {}).then((r) => r.data),
  createProject: (body) => client.post("/projects", body).then((r) => r.data),
  projectOverview: (code) => client.get(`/projects/${code}/overview`).then((r) => r.data),
  missions: (project_code) => client.get("/missions", { params: { project_code } }).then((r) => r.data),
  mission: (id) => client.get(`/missions/${id}`).then((r) => r.data),
  createProductionMission: (id) => client.post(`/missions/${id}/create-production-mission`, {}).then((r) => r.data),
  approvals: (status = "PENDING") => client.get("/approvals", { params: { status } }).then((r) => r.data),
  decideApproval: (id, action, note, expires_in_days) => client.post(`/approvals/${id}/decision`, { action, note, expires_in_days }).then((r) => r.data),
  crossProjectAccess: () => client.get("/cross-project-access").then((r) => r.data),
  revokeCrossProject: (id) => client.post(`/cross-project-access/${id}/revoke`, {}).then((r) => r.data),
  ptRegistry: () => client.get("/production-tools/registry").then((r) => r.data),
  ptProviders: () => client.get("/production-tools/providers").then((r) => r.data),
  ptCapabilities: () => client.get("/production-tools/capabilities").then((r) => r.data),
  ptPermissions: (agent_code) => client.get("/production-tools/permissions", { params: agent_code ? { agent_code } : {} }).then((r) => r.data),
  ptBudgets: () => client.get("/production-tools/budgets").then((r) => r.data),
  ptHealth: () => client.get("/production-tools/health").then((r) => r.data),
  ptRuns: (agent_code) => client.get("/production-tools/runs", { params: agent_code ? { agent_code } : {} }).then((r) => r.data),
  decisions: (project_code) => client.get("/decisions", { params: { project_code } }).then((r) => r.data),
  locks: (project_code) => client.get("/locks", { params: { project_code } }).then((r) => r.data),
  requestChange: (id, reason) => client.post(`/locks/${id}/request-change`, { reason }).then((r) => r.data),
  knowledge: (project_code) => client.get("/knowledge", { params: { project_code } }).then((r) => r.data),
  assets: (project_code) => client.get("/assets", { params: { project_code } }).then((r) => r.data),
  activity: (project_code) => client.get("/activity", { params: { project_code } }).then((r) => r.data),
  analytics: () => client.get("/analytics").then((r) => r.data),
  skills: () => client.get("/skills").then((r) => r.data),
  operatingModules: (status) => client.get("/operating-modules", { params: status ? { status } : {} }).then((r) => r.data),
  executionPlaybooks: (status) => client.get("/execution-playbooks", { params: status ? { status } : {} }).then((r) => r.data),
  jobs: (kind, status) => client.get("/jobs", { params: { kind, status } }).then((r) => r.data),
  tasks: (project_code, status) => client.get("/tasks", { params: { project_code, status } }).then((r) => r.data),
  createTask: (body) => client.post("/tasks", body).then((r) => r.data),
  updateTask: (id, body) => client.patch(`/tasks/${id}`, body).then((r) => r.data),
  task: (id) => client.get(`/tasks/${id}`).then((r) => r.data),
  saveTaskCheckpoint: (id, body) => client.post(`/tasks/${id}/checkpoint`, body).then((r) => r.data),
  resumeTask: (id) => client.post(`/tasks/${id}/resume`, {}).then((r) => r.data),
  prepareTaskHandoff: (id) => client.post(`/tasks/${id}/handoff`, {}).then((r) => r.data),
  taskHandoff: (id) => client.get(`/tasks/${id}/handoff`).then((r) => r.data),
  updateTaskHandoff: (id, body) => client.patch(`/tasks/${id}/handoff`, body).then((r) => r.data),
  verifyTaskHandoff: (id) => client.post(`/tasks/${id}/handoff/verify`, {}).then((r) => r.data),
  productionReadiness: () => client.get("/system/production-readiness").then((r) => r.data),
  githubConnectorStatus: () => client.get("/connectors/github/status").then((r) => r.data),
  githubConnectorProbe: () => client.get("/connectors/github/probe").then((r) => r.data),
  automationStatus: () => client.get("/automation/status").then((r) => r.data),
  automationSuggestions: (status = "OPEN") => client.get("/automation/suggestions", { params: { status } }).then((r) => r.data),
  decideAutomationSuggestion: (id, action) => client.post(`/automation/suggestions/${id}/decision`, { action }).then((r) => r.data),
  executionSyncStatus: () => client.get("/execution-sync/status").then((r) => r.data),
  syncExecutionGithub: (body = {}) => client.post("/execution-sync/github", body).then((r) => r.data),
  cancelJob: (kind, id) => client.post(`/jobs/${kind}/${id}/cancel`, {}).then((r) => r.data),
  retryJob: (kind, id) => client.post(`/jobs/${kind}/${id}/retry`, {}).then((r) => r.data),
  resolveRouting: (text, project_code) => client.post("/routing/resolve", { text, project_code }).then((r) => r.data),
  command: (text, project_code, idempotency_key, template_id) => client.post("/command", { text, project_code, idempotency_key, template_id }).then((r) => r.data),
  commandJob: (id) => client.get(`/command-jobs/${id}`).then((r) => r.data),
  migrationGateStatus: () => client.get("/migration-gate/status").then((r) => r.data),
  migrationGateConfirm: () => client.post("/migration-gate/confirm", {}).then((r) => r.data),
  migrationGateStart: () => client.post("/migration-gate/start-migration", {}).then((r) => r.data),
  migrationBatches: (project_code) => client.get("/migration-batches", { params: { project_code } }).then((r) => r.data),
  migrationBatch: (id) => client.get(`/migration-batches/${id}`).then((r) => r.data),
  migrationItemInput: (bid, key, body) => client.post(`/migration-batches/${bid}/items/${key}/input`, body).then((r) => r.data),
  migrationItemReviewed: (bid, key) => client.post(`/migration-batches/${bid}/items/${key}/reviewed`, {}).then((r) => r.data),
  migrationItemApprove: (bid, key) => client.post(`/migration-batches/${bid}/items/${key}/approve`, {}).then((r) => r.data),
  migrationItemReject: (bid, key, reason) => client.post(`/migration-batches/${bid}/items/${key}/reject`, { reason }).then((r) => r.data),
  templates: (includeArchived = false) => client.get(`/command-templates?include_archived=${includeArchived}`).then((r) => r.data),
  templateSuggestions: (project_code, page) => client.get("/command-templates/suggestions", { params: { project_code, page } }).then((r) => r.data),
  createTemplate: (body) => client.post("/command-templates", body).then((r) => r.data),
  updateTemplate: (id, body) => client.put(`/command-templates/${id}`, body).then((r) => r.data),
  duplicateTemplate: (id) => client.post(`/command-templates/${id}/duplicate`, {}).then((r) => r.data),
  archiveTemplate: (id) => client.post(`/command-templates/${id}/archive`, {}).then((r) => r.data),
  favoriteTemplate: (id) => client.post(`/command-templates/${id}/favorite`, {}).then((r) => r.data),
  workflows: () => client.get("/workflows").then((r) => r.data),
  createWorkflow: (body) => client.post("/workflows", body).then((r) => r.data),
  runWorkflow: (code, body) => client.post(`/workflows/${code}/run`, body).then((r) => r.data),
  workflowRuns: (project_code) => client.get("/workflow-runs", { params: { project_code } }).then((r) => r.data),
  workflowRun: (id) => client.get(`/workflow-runs/${id}`).then((r) => r.data),
};

export default client;
